/**
 * Institution Dashboard
 *
 * Main dashboard component for viewing institution financial health.
 * Features:
 * - Galaxy Score Display with animated rings
 * - Radar chart for CAMEL component visualization
 * - Interactive timeline for historical periods
 * - Expandable CAMEL indicator gauges
 * - AI-powered insights section
 * - Reports list with actions
 * - Optional institution selector (for /ratings page)
 *
 * Used by:
 * - BankUser/BankAdmin as their home dashboard (single institution)
 * - All roles on /ratings page with integrated selector
 */

import { useState, useMemo, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useInstitutionData } from './hooks/useInstitutionData';
import { calculateQuarterAverages, formatPeriodDate } from './utils';
import { camelLabels, categoryMapping } from './constants';
import type { CAMELCode } from './types';

// Component imports
import { GalaxyScoreDisplay } from './components/GalaxyScoreDisplay';
import { RadarChart } from './components/RadarChart';
import { InsightsSection } from './components/InsightsSection';
import { TimelineChartContainer } from './components/TimelineChart';
import { ReportsSection } from './components/ReportsSection';
import { LinearGauge } from './components/LinearGauge';
import { IndicatorRow } from './components/IndicatorRow';
import InstitutionSelector from '../../components/selector/InstitutionSelector';

import styles from './InstitutionDashboard.module.css';

interface PeriodInfo {
  label: string;
  date: string;
  isLatest: boolean;
  onReset: () => void;
}

interface InstitutionDashboardProps {
  /**
   * Optional institution ID override.
   * If not provided, uses context-based selection (role-dependent).
   */
  institutionId?: string;
  /**
   * Show institution selector in the header (for /ratings page)
   */
  showSelector?: boolean;
  /**
   * Hide the dashboard header (when period info is shown elsewhere)
   */
  hideHeader?: boolean;
  /**
   * Callback to receive period info for external display
   */
  onPeriodChange?: (period: PeriodInfo) => void;
}

export default function InstitutionDashboard({
  institutionId: explicitId,
  showSelector = false,
  hideHeader = false,
  onPeriodChange,
}: InstitutionDashboardProps) {
  const { t } = useTranslation();

  // State for interactive features
  const [selectedPeriodIndex, setSelectedPeriodIndex] = useState(0);
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});

  // Get institution data based on role or explicit ID
  const { data, institutionId, isBankUserView, isLoading } = useInstitutionData(explicitId);

  // Calculate derived values
  const selectedPeriod = data?.history?.[selectedPeriodIndex];
  const previousPeriod = data?.history?.[selectedPeriodIndex + 1] || null;
  const isLatestPeriod = selectedPeriodIndex === 0;

  // Calculate quarter averages for comparison
  const quarterAverages = useMemo(() => {
    if (!data?.history) return { finalScore: 0, camel: {} };
    return calculateQuarterAverages(data.history, selectedPeriodIndex);
  }, [data, selectedPeriodIndex]);

  // Group indicators by category for gauge expansion
  // Use selected period's indicators so they update when clicking historical periods
  const indicatorsByCategory = useMemo(() => {
    const indicators = selectedPeriod?.sankeyIndicators || data?.sankeyIndicators;
    if (!indicators) return {};
    const grouped: Record<string, typeof indicators> = {};
    indicators.forEach((ind) => {
      const cat = ind.indicator_category;
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(ind);
    });
    return grouped;
  }, [data, selectedPeriod]);

  // Toggle category expansion
  const toggleCategory = (code: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [code]: !prev[code],
    }));
  };

  // Handle period selection from timeline
  const handleSelectPeriod = (index: number) => {
    setSelectedPeriodIndex(index);
  };

  // Reset to latest period
  const handleResetToLatest = useCallback(() => {
    setSelectedPeriodIndex(0);
  }, []);

  // Notify parent of period changes (for external display)
  useEffect(() => {
    if (onPeriodChange && selectedPeriod) {
      onPeriodChange({
        label: isLatestPeriod ? t('institutionDashboard.currentPeriod') : t('institutionDashboard.historicalPeriod'),
        date: formatPeriodDate(selectedPeriod.date),
        isLatest: isLatestPeriod,
        onReset: handleResetToLatest,
      });
    }
  }, [onPeriodChange, selectedPeriod, isLatestPeriod, handleResetToLatest, t]);

  // Loading state
  if (isLoading) {
    return (
      <div className={styles.dashboard}>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner} />
          <p>{t('institutionDashboard.loadingDashboard')}</p>
        </div>
      </div>
    );
  }

  // No data state (for non-bank users without selection)
  if (!data || !selectedPeriod) {
    return (
      <div className={styles.dashboard}>
        {showSelector && (
          <header className={styles.dashboardHeader}>
            <div className={styles.headerLeft}>
              <h1 className={styles.headerTitle}>{t('institutionDashboard.ratings')}</h1>
              <p className={styles.institutionName}>{t('institutionDashboard.viewAndAnalyze')}</p>
            </div>
            <div className={styles.selectorWrapper}>
              <InstitutionSelector />
            </div>
          </header>
        )}
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>📊</div>
          <h3>{t('institutionDashboard.noInstitutionSelected')}</h3>
          <p>
            {isBankUserView
              ? t('institutionDashboard.institutionDataNotAvailable')
              : t('institutionDashboard.selectInstitutionToView')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.dashboard}>
      {/* Header - hidden when period info is shown externally */}
      {!hideHeader && (
        <header className={styles.dashboardHeader}>
          <div className={styles.headerLeft}>
            <h1 className={styles.headerTitle}>{data.institution_name}</h1>
            <div
              className={styles.periodBadge}
              style={{ borderColor: isLatestPeriod ? 'var(--rating-aa)' : 'var(--chart-line-primary)' }}
            >
              <span className={styles.periodLabel}>
                {isLatestPeriod ? t('institutionDashboard.currentPeriod') : t('institutionDashboard.historicalPeriod')}
              </span>
              <span className={styles.periodDate}>{formatPeriodDate(selectedPeriod.date)}</span>
            </div>
            {!isLatestPeriod && (
              <button className={styles.resetButton} onClick={handleResetToLatest}>
                ↺ {t('institutionDashboard.backToCurrent')}
              </button>
            )}
          </div>
          {showSelector && (
            <div className={styles.selectorWrapper}>
              <InstitutionSelector />
            </div>
          )}
        </header>
      )}

      {/* Main Content */}
      <div className={styles.mainContent}>
        {/* Left Column */}
        <div className={styles.leftColumn}>
          <div className={styles.topRow}>
            {/* Radar Chart */}
            <div className={styles.chartCard}>
              <RadarChart camelData={selectedPeriod.camel} />
            </div>

            {/* AI Insights */}
            <div className={styles.chartCard}>
              <InsightsSection
                institutionId={institutionId || ''}
                currentPeriod={selectedPeriod}
                previousPeriod={previousPeriod}
              />
            </div>
          </div>

          {/* Timeline Chart */}
          <div className={styles.timelineCard}>
            <TimelineChartContainer
              history={data.history}
              institution={{ id: institutionId || '', name: data.institution_name }}
              selectedIndex={selectedPeriodIndex}
              onSelectPeriod={handleSelectPeriod}
            />
          </div>

          {/* Reports Section */}
          <div className={styles.reportsCard}>
            <ReportsSection institutionId={institutionId || ''} />
          </div>
        </div>

        {/* Right Column */}
        <div className={styles.rightColumn}>
          {/* Galaxy Score Display */}
          <GalaxyScoreDisplay
            score={selectedPeriod.finalScore}
            rating={selectedPeriod.rating}
            previousScore={previousPeriod?.finalScore ?? selectedPeriod.finalScore}
          />

          {/* CAMEL Indicators Section */}
          <div className={styles.sectionHeader}>
            <span className={styles.sectionTitle}>{t('camel.title')}</span>
            <span className={styles.sectionSubtitle}>{t('institutionDashboard.clickToExpand')}</span>
          </div>

          <div className={styles.gaugesContainer}>
            {selectedPeriod.camel.map((camelItem) => {
              const code = camelItem.code as CAMELCode;
              const label = camelLabels[code];
              const categoryName = categoryMapping[code];
              const indicators = indicatorsByCategory[categoryName] || [];
              const isExpanded = expandedCategories[camelItem.code] || false;
              const quarterAvg = quarterAverages.camel[camelItem.code] || camelItem.score;

              return (
                <LinearGauge
                  key={camelItem.code}
                  label={label.shortName}
                  currentValue={camelItem.score}
                  quarterAvg={quarterAvg}
                  max={label.max}
                  isExpanded={isExpanded}
                  onToggle={() => toggleCategory(camelItem.code)}
                >
                  {indicators.map((indicator) => (
                    <IndicatorRow key={indicator.indicator_id} indicator={indicator} />
                  ))}
                </LinearGauge>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
